import { motion } from 'framer-motion';
import { PageTransition, FadeIn, StaggerContainer, StaggerItem } from '../components/PageTransition';
import { BackButton } from '../components/BackButton';
import { StaticCard, TrainingReferenceLink } from '../components/BorderCard';
import { Check, X } from 'lucide-react';

const sections = [
  'Uniform Standards',
  'Footwear Guidelines',
  'Hair and Grooming',
  'Hands and Nails',
  'Accessories',
  'Fragrance and Hygiene',
];

const dosAndDonts = {
  dos: [
    'Maintain clean, pressed uniform',
    'Keep nails clean and trimmed',
    'Use subtle fragrance',
    'Polish shoes daily',
    'Keep hair neat and styled',
  ],
  donts: [
    'Wear excessive jewelry',
    'Use strong perfumes',
    'Leave uniform wrinkled',
    'Wear casual footwear',
    'Leave hair unkempt',
  ]
};

const commitmentValues = [
  'Consistency',
  'Honesty',
  'Punctuality',
  'Ethical Behaviour',
  'Professional Conduct',
  'Accountability',
  'Trustworthiness',
  'Ownership',
  'Respect for People and Product'
];

const GroomingPage = () => {
  return (
    <PageTransition className="min-h-screen bg-[#F8F6F2] py-24 px-6">
      <div className="grain-overlay" />
      <BackButton to="/what" label="Back" />

      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-xs uppercase tracking-[0.3em] text-[#1A1A1A]/60 mb-4">
            Training Module
          </h2>
          <h1 className="text-4xl md:text-5xl font-light tracking-tight text-[#1A1A1A] mb-4">
            Grooming Standards
          </h1>
          <TrainingReferenceLink 
            href="https://www.canva.com/design/DAG6QYiEIq4/Te54gLSqFMHC5VuQjDOfyQ/view?utm_content=DAG6QYiEIq4&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=hb17b973308"
          />
        </motion.div>

        {/* Sections Grid */}
        <FadeIn delay={0.2}>
          <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-center text-[#1A1A1A]/60 mb-8">
            Standards Covered
          </h3>
        </FadeIn>
        
        <StaggerContainer className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-16">
          {sections.map((section, index) => (
            <StaggerItem key={index}>
              <StaticCard className="p-6 text-center">
                <span className="text-sm uppercase tracking-[0.1em] text-[#1A1A1A]">
                  {section}
                </span>
              </StaticCard>
            </StaggerItem>
          ))}
        </StaggerContainer>

        {/* Do's and Don'ts */}
        <FadeIn delay={0.4}>
          <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-center text-[#1A1A1A]/60 mb-8">
            Do's and Don'ts
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            {/* Do's */}
            <StaticCard className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-green-500/10 border border-green-500 flex items-center justify-center">
                  <Check size={16} className="text-green-600" />
                </div>
                <h4 className="text-sm uppercase tracking-[0.15em] font-semibold text-[#1A1A1A]">
                  Do's
                </h4>
              </div>
              <ul className="space-y-3">
                {dosAndDonts.dos.map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-sm text-[#1A1A1A]">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="text-[9px] uppercase tracking-wider text-[#1A1A1A]/30 text-center mt-6">
                For reference only
              </div>
            </StaticCard>

            {/* Don'ts */}
            <StaticCard className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-red-500/10 border border-red-500 flex items-center justify-center">
                  <X size={16} className="text-red-600" />
                </div>
                <h4 className="text-sm uppercase tracking-[0.15em] font-semibold text-[#1A1A1A]">
                  Don'ts
                </h4>
              </div>
              <ul className="space-y-3">
                {dosAndDonts.donts.map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-sm text-[#1A1A1A]">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="text-[9px] uppercase tracking-wider text-[#1A1A1A]/30 text-center mt-6">
                For reference only
              </div>
            </StaticCard>
          </div>
        </FadeIn>

        {/* Commitment Values */}
        <FadeIn delay={0.5}>
          <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-center text-[#1A1A1A]/60 mb-8">
            Commitment Values
          </h3>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-12">
            {commitmentValues.map((value, index) => (
              <motion.div 
                key={index}
                className="bg-white border border-[rgba(26,26,26,0.08)] p-4 text-center cursor-default"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 * index }}
              >
                <span className="text-xs uppercase tracking-[0.1em] font-medium text-[#1A1A1A]">
                  {value}
                </span>
              </motion.div>
            ))}
          </div>
        </FadeIn>

        {/* Professional Principle */}
        <FadeIn delay={0.6}>
          <StaticCard className="max-w-xl mx-auto text-center py-8 px-8">
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1A1A1A]/60 mb-4">
              Professional Grooming Principle
            </h4>
            <p className="text-lg font-light text-[#1A1A1A] italic">
              "Your appearance is the first touchpoint of the brand experience."
            </p>
          </StaticCard>
        </FadeIn>
      </div>
    </PageTransition>
  );
};

export default GroomingPage;
